import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";

function formatTime12(t) {
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${period}`;
}

function slotEndTime(t) {
  const [h, m] = t.split(":").map(Number);
  const endH = (h + 1) % 24;
  return `${String(endH).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// Maps midnight-wrap slots to sortable positions (00→24, 01→25, ..., 04→28)
function slotHour(t) {
  const h = parseInt(t, 10);
  return h < 5 ? h + 24 : h;
}

// Animated SVG checkmark drawn with a stroke-dashoffset path
function AnimatedCheckmark() {
  return (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 0.15, type: "spring", stiffness: 220, damping: 18 }}
      className="flex items-center justify-center mx-auto mb-8"
      style={{ width: 96, height: 96 }}
    >
      {/* Outer circle */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.15, type: "spring", stiffness: 220, damping: 18 }}
        className="absolute"
        style={{
          width: 96, height: 96, borderRadius: "50%",
          background: "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
          border: "3px solid #22c55e",
        }}
      />
      <motion.svg
        viewBox="0 0 52 52"
        style={{ width: 52, height: 52, position: "relative", zIndex: 1 }}
      >
        <motion.path
          d="M14 27 L22 35 L38 18"
          fill="none"
          stroke="#16a34a"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ delay: 0.45, duration: 0.5, ease: "easeOut" }}
        />
      </motion.svg>
    </motion.div>
  );
}

function DetailRow({ label, value, gold }) {
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-[10px] font-black text-black/35 uppercase tracking-[0.18em]">{label}</p>
      <p className={`font-semibold text-sm leading-snug ${gold ? "text-[#d97706] font-black" : "text-black"}`}>
        {value}
      </p>
    </div>
  );
}

export default function Confirmation() {
  const navigate  = useNavigate();
  const { state } = useLocation();
  const booking   = state?.booking;

  // Fire confetti on mount
  useEffect(() => {
    const t = setTimeout(() => {
      confetti({
        particleCount: 140,
        spread: 80,
        origin: { y: 0.55 },
        colors: ["#f59e0b", "#fbbf24", "#22c55e", "#86efac", "#ffffff", "#000000"],
      });
    }, 400);
    return () => clearTimeout(t);
  }, []);

  // No-booking fallback
  if (!booking) {
    return (
      <div className="min-h-screen bg-white" style={{ paddingTop: 64 }}>
        <div className="flex flex-col items-center justify-center min-h-[70vh] px-4 text-center">
          <p className="text-black/40 mb-6 text-base">No booking found.</p>
          <button
            onClick={() => navigate("/book")}
            className="bg-[#f59e0b] hover:bg-amber-400 text-black font-black px-8 py-3 rounded-2xl transition-all"
          >
            Book a Slot
          </button>
        </div>
      </div>
    );
  }

  const allSlots = [
    ...(booking.slots || []),
    ...(booking.nextDaySlots || []),
  ].sort((a, b) => slotHour(a) - slotHour(b));
  const hasNextDay = (booking.nextDaySlots || []).length > 0;
  const dateLabel = new Date(booking.date + "T00:00:00").toLocaleDateString("en-IN", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
  const nextDayLabel = (() => {
    if (!hasNextDay || !booking.date) return "";
    const d = new Date(booking.date + "T00:00:00");
    d.setDate(d.getDate() + 1);
    return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "long" });
  })();
  const timeRange = allSlots.length
    ? `${formatTime12(allSlots[0])} → ${formatTime12(slotEndTime(allSlots[allSlots.length - 1]))}${hasNextDay ? ` (into ${nextDayLabel})` : ""}`
    : "—";

  const whatsappMsg = encodeURIComponent(
`🏏 I just booked a slot at Town Square Arena!

Sport: ${booking.sport || "Oval Cricket"}
Date: ${dateLabel}
Time: ${timeRange}
Booking ID: ${booking.id}

📍 Location: Town Square Suites, Shamshabad - Mamidipally Rd, Shamshabad, Hyderabad, Telangana 501218

🔗 Book your slot: townsquarearena.com`
  );
  const whatsappUrl = `https://wa.me/?text=${whatsappMsg}`;

  return (
    <div className="bg-white min-h-screen" style={{ paddingTop: 64 }}>
      <div className="max-w-lg mx-auto px-4 py-12">

        {/* ── Checkmark ── */}
        <div className="flex justify-center" style={{ position: "relative" }}>
          <AnimatedCheckmark />
        </div>

        {/* ── Heading ── */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-8"
        >
          <h1
            className="text-black font-black text-3xl sm:text-4xl tracking-tight mb-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Booking Confirmed!
          </h1>
          <p className="text-black/45 text-sm">Your slot has been reserved. See you on the pitch! 🏏</p>

          {/* Booking ID */}
          <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full">
            <span className="text-[10px] font-black text-black/40 uppercase tracking-widest">Booking ID</span>
            <span className="text-[#d97706] font-black text-sm font-mono">{booking.id}</span>
          </div>
        </motion.div>

        {/* ── Details card ── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.62, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-3xl border border-black/8 overflow-hidden mb-6"
          style={{ background: "#f9fafb" }}
        >
          {/* Green header stripe */}
          <div className="bg-green-500 px-5 py-3 flex items-center justify-between">
            <span className="text-white font-black text-sm tracking-wide">Payment Successful</span>
            <span className="bg-white/20 text-white text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest">
              Confirmed
            </span>
          </div>

          {/* Details grid */}
          <div className="p-5 grid grid-cols-2 gap-x-6 gap-y-5">
            <DetailRow label="Sport"        value={booking.sport || "Oval Cricket"} />
            <DetailRow label="Name"         value={booking.name} />
            <DetailRow label="Date"         value={dateLabel} />
            <DetailRow label="Time"         value={timeRange} />
            <DetailRow label="Slots Booked" value={`${allSlots.length} hr${allSlots.length > 1 ? "s" : ""}`} />
            <DetailRow label="Amount Paid"  value={`₹${Number(booking.total).toLocaleString()}`} gold />
          </div>

          {/* Payment ID */}
          {booking.razorpay_payment_id && (
            <div className="border-t border-black/6 px-5 py-3">
              <p className="text-[10px] font-black text-black/30 uppercase tracking-widest mb-0.5">Payment Reference</p>
              <p className="text-black/50 text-xs font-mono">{booking.razorpay_payment_id}</p>
            </div>
          )}
        </motion.div>

        {/* ── Action buttons ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.78, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col sm:flex-row gap-3"
        >
          {/* Book Another */}
          <button
            onClick={() => navigate("/book")}
            className="flex-1 bg-[#f59e0b] hover:bg-amber-400 text-black font-black text-sm py-3.5 rounded-2xl transition-all duration-200 hover:shadow-lg hover:shadow-[#f59e0b]/25 hover:-translate-y-0.5"
          >
            Book Another Slot →
          </button>

          {/* WhatsApp share */}
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noreferrer"
            className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#22c55e] text-white font-black text-sm py-3.5 rounded-2xl transition-all duration-200 hover:shadow-lg hover:shadow-green-500/25 hover:-translate-y-0.5"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 flex-shrink-0">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Share on WhatsApp
          </a>
        </motion.div>

        {/* Bottom note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="text-black/25 text-xs text-center mt-6"
        >
          A confirmation will be sent via WhatsApp to {booking.phone}
        </motion.p>

      </div>
    </div>
  );
}
